import { TessaAiChatPart, TessaChatMessage } from "@gettactic/api";
import { TessaResponseMessages } from "@webV2/pages/Tessa/types";
import React from "react";
import { useTacticHooks } from "@gettactic/hooks";

export type TessaMessageState = "completed" | "cancelled";

type StateDataMap = {
  completed: {
    data: any;
  };
  cancelled: {
    reason: string;
    abortedByUser: boolean;
    data: any;
  };
};

export interface UseTessaMessageStateReturn {
  state: TessaMessageState | null | undefined;
  data: StateDataMap[TessaMessageState] | null | undefined;
  setMessageState: <S extends TessaMessageState>(
    status: S,
    data: StateDataMap[S]
  ) => Promise<boolean>;
  getMessageState: () => { state: TessaMessageState | null; data: any } | null;
}

export const useTessaMessageState = (
  chatId: string | undefined | null,
  message: TessaChatMessage | TessaResponseMessages | undefined,
  part?: TessaAiChatPart
): UseTessaMessageStateReturn | null => {
  const { apiClient, queryClient } = useTacticHooks();
  if (!message) return null;

  function getMessageState<S extends TessaMessageState>(): {
    state: S;
    data: StateDataMap[S];
  } | null {
    if (message && "state" in message) {
      return (message.state as never) || null;
    }

    return null;
  }

  const messageState = getMessageState();
  const [state, setState] = React.useState<TessaMessageState | null>(
    messageState?.state || null
  );
  const [data, setData] = React.useState<
    StateDataMap[TessaMessageState] | null
  >(messageState?.data || null);

  const isLiveMessageToolId = !!part?.toolCallId;

  async function setMessageState<S extends TessaMessageState>(
    status: S,
    data: StateDataMap[S]
  ) {
    const messageId =
      part?.toolCallId || (message as TessaChatMessage)?.tool_id
        ? part?.toolCallId || (message as TessaChatMessage)?.tool_id
        : message?.id;

    if (!messageId || !chatId) return false;

    await apiClient.tessa.setMessageState(chatId, messageId, {
      state: status,
      data
    });

    await queryClient.invalidateQueries(["tessa.chat.messages", chatId]);

    setState(status);
    setData(data);
    return true;
  }

  return {
    state: isLiveMessageToolId ? state : messageState?.state,
    data: isLiveMessageToolId ? data : messageState?.data,
    setMessageState,
    getMessageState
  };
};
